import { useImageStore } from '@/entities/image/store';
import type { StagedChange } from '@/shared/types';

export default function DiffModal() {
  const showDiffPreview = useImageStore((s) => s.showDiffPreview);
  const setShowDiffPreview = useImageStore((s) => s.setShowDiffPreview);
  const stagedChanges = useImageStore((s) => s.stagedChanges);
  const resetStagedChanges = useImageStore((s) => s.resetStagedChanges);

  if (!showDiffPreview) return null;

  const entries = Array.from(stagedChanges.entries());

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-gray-800 w-full max-w-2xl max-h-[80vh] flex flex-col rounded-xl shadow-2xl border border-gray-700">
        <div className="flex items-center justify-between px-6 pt-4 pb-0 border-b border-gray-700">
          <h2 className="text-lg font-semibold">Changes Preview</h2>
          <button
            onClick={() => setShowDiffPreview(false)}
            className="text-gray-400 hover:text-white p-2 rounded-full hover:bg-gray-700 transition"
          >
            ✕
          </button>
        </div>

        {entries.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-gray-500 text-sm p-8">
            No staged changes
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {entries.map(([imgPath, changes]) => (
              <div key={imgPath} className="space-y-2">
                <h3 className="text-xs font-mono text-gray-400 truncate">
                  {imgPath.split('/').pop()}
                </h3>
                {Array.from(changes.values()).map((ch) => (
                  <DiffRow key={ch.fieldKey} change={ch} imgPath={imgPath} />
                ))}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-end px-6 py-4 border-t border-gray-700">
          <button
            onClick={() => resetStagedChanges()}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded text-sm font-medium transition mr-2"
          >
            Reset All
          </button>
        </div>
      </div>
    </div>
  );
}

function DiffRow({ change, imgPath }: { change: StagedChange; imgPath: string }) {
  const unstageChange = useImageStore((s) => s.unstageChange);

  return (
    <div className="group flex items-center justify-between gap-4 p-2 hover:bg-gray-700/50 rounded-lg transition-colors">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1">
          {change.fieldKey}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-red-400/80 line-through truncate text-xs">
            {String(change.oldValue ?? 'empty')}
          </span>
          <span className="text-gray-600">→</span>
           <span className="text-green-400 truncate text-xs">
             {change.newValue === '' ? '(cleared)' : String(change.newValue ?? 'empty')}
           </span>
        </div>
      </div>
      <button
        onClick={() => unstageChange(imgPath, change.fieldKey)}
        title="Reset this field"
        className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-gray-600 rounded text-gray-400 hover:text-red-400 transition-all"
      >
        ✕
      </button>
    </div>
  );
}
